import { useState, type FormEvent, type ReactElement } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { MapPin } from "lucide-react";
import useEnforceLightMode from "../hooks/useEnforceLightMode";
import usePageBackground from "../hooks/usePageBackground";
import { LANDING_FEATURES, type MockProduct } from "../utils/landingFeatures";
import { getProductImageUrl } from "../utils/imageUtils";

const DISPLAY = "font-[Nunito,sans-serif] tracking-[-0.02em]";
const DARK = "#201B33";
const RETAILERS = ["Walmart", "Target", "Costco", "Lowe's", "Best Buy", "Dollar General"];
const DEALS = LANDING_FEATURES[0].products;
const FEATURED = DEALS[0];
const HAULS = [
  { image: "/TestimonialImages/deal-1.png", saved: 268 },
  { image: "/TestimonialImages/deal-2.png", saved: 141 },
  { image: "/TestimonialImages/deal-3.png", saved: 86 },
  { image: "/TestimonialImages/deal-4.png", saved: 312 },
];

const OPTIONS = [
  { id: "feed", label: "Feed first" },
  { id: "hunter", label: "Dark hunter" },
  { id: "local", label: "Local find" },
  { id: "haul", label: "Price smash" },
  { id: "current", label: "Current" },
] as const;

type OptionId = (typeof OPTIONS)[number]["id"];

const isOption = (value: string | null): value is OptionId =>
  OPTIONS.some((option) => option.id === value);

function pct(product: MockProduct) {
  return Math.round(((product.msrp - product.price) / product.msrp) * 100);
}

function money(n: number) {
  return n % 1 === 0 ? `$${n.toFixed(0)}` : `$${n.toFixed(2)}`;
}

function Header({ dark }: { dark?: boolean }) {
  return (
    <header className={`border-b ${dark ? "border-white/10" : "border-slate-100 bg-white"}`}>
      <div className="mx-auto flex h-16 max-w-6xl items-center justify-between gap-4 px-4 sm:h-[4.5rem] sm:px-6">
        <Link to="/" className="min-w-0 hover:opacity-80">
          <img
            src={dark ? "/logo_main_white.svg" : "/logo_main.svg"}
            alt="DealHawk"
            className="h-8 w-auto sm:h-9"
          />
        </Link>
        <nav className={`hidden items-center gap-8 text-[15px] font-semibold md:flex ${dark ? "text-white/70" : "text-slate-700"}`}>
          <span>Features</span>
          <span>Hauls</span>
          <span>Pricing</span>
        </nav>
        <div className="flex items-center gap-3">
          <Link
            to="/deals"
            className={`hidden text-sm font-extrabold sm:inline-flex ${dark ? "text-violet-200" : "text-[#7C3AED]"}`}
          >
            Browse free online deals
          </Link>
          <Link
            to="/get-started"
            className="inline-flex h-10 items-center rounded-xl bg-[#7C3AED] px-4 text-sm font-bold text-white sm:h-11 sm:px-6"
          >
            Log in
          </Link>
        </div>
      </div>
    </header>
  );
}

function Retailers({ dark }: { dark?: boolean }) {
  return (
    <div className="mx-auto max-w-5xl px-4 pb-10 pt-8 text-center sm:px-6">
      <p className={`text-xs font-bold uppercase tracking-[0.25em] ${dark ? "text-white/40" : "text-slate-500"}`}>
        Finding deals at
      </p>
      <div className="mt-4 flex flex-wrap items-center justify-center gap-x-8 gap-y-2">
        {RETAILERS.map((name) => (
          <span
            key={name}
            className={`text-base font-black sm:text-lg ${dark ? "text-white/55" : "text-slate-500"}`}
          >
            {name}
          </span>
        ))}
      </div>
    </div>
  );
}

function DealTile({ product, dark }: { product: MockProduct; dark?: boolean }) {
  return (
    <article className={`rounded-2xl p-3 ${dark ? "bg-white/[0.06]" : "border border-slate-200 bg-white"}`}>
      <div className="flex gap-3">
        <div className="h-[72px] w-[72px] shrink-0 overflow-hidden rounded-xl bg-white">
          <img src={getProductImageUrl(product.image, "400")} alt="" className="h-full w-full object-contain p-1" />
        </div>
        <div className="min-w-0">
          <p className={`line-clamp-2 text-sm font-bold leading-snug ${dark ? "text-white" : "text-[#1B1526]"}`}>
            {product.name}
          </p>
          <p className={`mt-1 truncate text-[11px] ${dark ? "text-white/40" : "text-slate-500"}`}>
            {product.storeLabel}
            {product.aisle ? ` · Aisle ${product.aisle}` : ""}
          </p>
          <div className="mt-1.5 flex flex-wrap items-baseline gap-1.5">
            <span className="text-lg font-black text-emerald-500">{money(product.price)}</span>
            <span className={`text-xs line-through ${dark ? "text-white/30" : "text-slate-400"}`}>
              {money(product.msrp)}
            </span>
            <span className={`text-[10px] font-bold ${dark ? "text-violet-300" : "text-violet-700"}`}>
              {pct(product)}% off
            </span>
          </div>
        </div>
      </div>
    </article>
  );
}

function ZipHunt({ dark, label }: { dark?: boolean; label: string }) {
  const [zip, setZip] = useState("");
  const go = (event: FormEvent) => {
    event.preventDefault();
    window.location.assign("/get-started");
  };
  return (
    <form onSubmit={go} className="flex w-full max-w-md flex-col gap-2 sm:flex-row">
      <input
        value={zip}
        onChange={(event) => setZip(event.target.value)}
        inputMode="numeric"
        autoComplete="postal-code"
        placeholder="ZIP code"
        aria-label="ZIP code"
        className={`h-14 flex-1 rounded-2xl px-4 text-base font-semibold outline-none ${
          dark
            ? "border border-white/15 bg-white/10 text-white placeholder:text-white/40"
            : "border border-slate-200 bg-white text-[#1B1526] placeholder:text-slate-400"
        }`}
      />
      <button
        type="submit"
        className="h-14 rounded-2xl bg-[#7C3AED] px-6 text-lg font-extrabold text-white"
      >
        {label}
      </button>
    </form>
  );
}

function Stats({ dark }: { dark?: boolean }) {
  const items = [
    ["$2.4M", "saved this year"],
    ["12,400", "members"],
    ["82%", "avg. markdown"],
  ];
  return (
    <div className="flex flex-wrap items-center justify-center gap-y-3 lg:justify-start">
      {items.map(([value, label], i) => (
        <div key={label} className="flex items-center">
          {i > 0 && <span className={`mx-6 hidden h-5 w-px sm:block ${dark ? "bg-white/15" : "bg-slate-200"}`} />}
          <p className={`px-3 text-[15px] sm:px-0 ${dark ? "text-white/50" : "text-slate-500"}`}>
            <span className={`font-black ${dark ? "text-white" : "text-[#1B1526]"}`}>{value}</span> {label}
          </p>
        </div>
      ))}
    </div>
  );
}

function FeedHero() {
  return (
    <section className="bg-white">
      <Header />
      <div className="mx-auto grid max-w-6xl items-center gap-12 px-4 pb-4 pt-10 sm:px-6 lg:grid-cols-[0.9fr_1.1fr] lg:gap-14 lg:pt-14">
        <div className="text-center lg:text-left">
          <p className="inline-flex rounded-full border border-emerald-100 bg-emerald-50 px-4 py-2 text-sm font-bold text-emerald-800">
            Hidden in-store · not on the website
          </p>
          <h1 className={`mt-7 text-5xl font-black leading-[1.02] tracking-[-0.03em] sm:text-6xl lg:text-[4.25rem]`}>
            The clearance they don't put online.
          </h1>
          <p className="mx-auto mt-6 max-w-xl text-lg text-slate-600 sm:text-xl lg:mx-0">
            Live shelf prices, real stock, aisle-level directions at stores near you.
          </p>
          <div className="mt-8 flex justify-center lg:justify-start">
            <ZipHunt label="Show my deals" />
          </div>
          <div className="mt-9">
            <Stats />
          </div>
        </div>
        <div className="grid gap-3 sm:grid-cols-2">
          {DEALS.slice(0, 4).map((product) => (
            <DealTile key={product.upc} product={product} />
          ))}
        </div>
      </div>
      <Retailers />
    </section>
  );
}

function HunterHero() {
  return (
    <section className="text-white" style={{ backgroundColor: DARK }}>
      <Header dark />
      <div className="mx-auto grid max-w-6xl items-center gap-12 px-4 pb-4 pt-10 sm:px-6 lg:grid-cols-[1.05fr_0.95fr] lg:pt-14">
        <div className="text-center lg:text-left">
          <p className="inline-flex rounded-full border border-emerald-300/20 bg-emerald-300/10 px-4 py-2 text-sm font-bold text-emerald-200">
            For people who actually go
          </p>
          <h1 className="mt-7 text-5xl font-black leading-[1.02] tracking-[-0.03em] sm:text-6xl lg:text-[4.25rem]">
            Retailers hide the <span className="text-emerald-300">90% off</span>.
          </h1>
          <p className="mx-auto mt-6 max-w-xl text-lg text-slate-300 sm:text-xl lg:mx-0">
            We watch the shelf all day. You walk in before the crowd.
          </p>
          <div className="mt-8 flex flex-col items-center justify-center gap-3 sm:flex-row lg:justify-start">
            <Link
              to="/get-started"
              className="inline-flex h-14 w-full items-center justify-center rounded-2xl bg-[#7C3AED] px-8 text-lg font-extrabold text-white sm:w-auto"
            >
              Start hunting
            </Link>
            <Link
              to="/deals"
              className="inline-flex h-14 w-full items-center justify-center rounded-2xl border border-white/20 px-8 text-lg font-extrabold text-white sm:w-auto"
            >
              See a deal near you
            </Link>
          </div>
          <div className="mt-9">
            <Stats dark />
          </div>
        </div>
        <div className="space-y-3">
          {DEALS.slice(0, 3).map((product) => (
            <DealTile key={product.upc} product={product} dark />
          ))}
        </div>
      </div>
      <Retailers dark />
    </section>
  );
}

function LocalHero() {
  return (
    <section className="bg-white">
      <Header />
      <div className="mx-auto grid max-w-6xl items-center gap-12 px-4 pb-4 pt-10 sm:px-6 lg:grid-cols-[1.05fr_0.95fr] lg:pt-14">
        <div className="text-center lg:text-left">
          <p className="inline-flex items-center gap-1.5 rounded-full border border-violet-100 bg-violet-50 px-4 py-2 text-sm font-bold text-violet-800">
            <MapPin className="h-3.5 w-3.5" />
            Near you right now
          </p>
          <h1 className="mt-7 text-5xl font-black leading-[1.02] tracking-[-0.03em] sm:text-6xl lg:text-[3.75rem]">
            Your nearest store has this for $38.
          </h1>
          <p className="mx-auto mt-6 max-w-xl text-lg text-slate-600 sm:text-xl lg:mx-0">
            {FEATURED.name} · was $149 · Aisle A12 · 4 in stock · 2.1 miles
          </p>
          <div className="mt-8 flex justify-center lg:justify-start">
            <ZipHunt label="Find mine" />
          </div>
        </div>
        <article className="overflow-hidden rounded-[2rem] border border-slate-200 bg-white">
          <div className="flex aspect-[4/3] items-center justify-center bg-[#F6F4FA] p-8">
            <img src={getProductImageUrl(FEATURED.image, "400")} alt="" className="max-h-full max-w-full object-contain" />
          </div>
          <div className="p-6">
            <p className="text-sm font-bold text-slate-500">{FEATURED.storeLabel}</p>
            <p className="mt-1 text-xl font-black">{FEATURED.name}</p>
            <div className="mt-3 flex flex-wrap items-baseline gap-2">
              <span className="text-4xl font-black text-emerald-600">{money(FEATURED.price)}</span>
              <span className="text-lg text-slate-400 line-through">{money(FEATURED.msrp)}</span>
              <span className="rounded-full bg-emerald-50 px-2.5 py-1 text-sm font-bold text-emerald-800">
                {pct(FEATURED)}% off
              </span>
            </div>
          </div>
        </article>
      </div>
      <Retailers />
    </section>
  );
}

function HaulHero() {
  return (
    <section className="bg-white">
      <Header />
      <div className="mx-auto grid max-w-6xl items-center gap-12 px-4 pb-4 pt-10 sm:px-6 lg:grid-cols-[1.1fr_0.9fr] lg:pt-14">
        <div className="text-center lg:text-left">
          <p className="inline-flex rounded-full border border-emerald-100 bg-emerald-50 px-4 py-2 text-sm font-bold text-emerald-800">
            50% off your first month · cancel anytime
          </p>
          <p className="mt-8 text-[11px] font-bold uppercase tracking-[0.22em] text-slate-400">
            One find covers the month
          </p>
          <h1 className="mt-3 flex flex-wrap items-end justify-center gap-4 leading-none lg:justify-start">
            <span className="text-7xl font-black tracking-[-0.05em] text-[#7C3AED] sm:text-8xl">$5</span>
            <span className="mb-2 text-4xl font-black text-slate-300 line-through sm:text-5xl">$50</span>
          </h1>
          <p className="mx-auto mt-6 max-w-xl text-lg text-slate-600 sm:text-xl lg:mx-0">
            Pay $5 for the $50 thing. Members share real hauls — not stock photos.
          </p>
          <div className="mt-8 flex flex-col items-center justify-center gap-3 sm:flex-row lg:justify-start">
            <Link
              to="/get-started"
              className="inline-flex h-14 w-full items-center justify-center rounded-2xl bg-[#7C3AED] px-8 text-lg font-extrabold text-white sm:w-auto"
            >
              Get 50% off first month
            </Link>
            <Link
              to="/deals"
              className="inline-flex h-14 w-full items-center justify-center rounded-2xl border border-slate-200 px-8 text-lg font-extrabold sm:w-auto"
            >
              See free online deals
            </Link>
          </div>
        </div>
        <div className="grid grid-cols-2 gap-3">
          {HAULS.map((haul) => (
            <div key={haul.image} className="relative aspect-[4/5] overflow-hidden rounded-2xl">
              <img src={haul.image} alt="" className="h-full w-full object-cover" />
              <span
                className="absolute right-2 top-2 rounded-full px-2.5 py-1 text-xs font-bold text-white"
                style={{ backgroundColor: "#1D6F4C" }}
              >
                saved ${haul.saved}
              </span>
            </div>
          ))}
        </div>
      </div>
      <Retailers />
    </section>
  );
}

function CurrentHero() {
  return (
    <section className="bg-white">
      <Header />
      <div className="mx-auto grid max-w-6xl items-center gap-12 px-4 pt-10 sm:px-6 lg:grid-cols-[1.15fr_0.85fr] lg:pt-14">
        <div className="text-center lg:text-left">
          <p className="inline-flex rounded-full border border-emerald-100 bg-emerald-50 px-4 py-2 text-sm font-bold text-emerald-800">
            50% off your first month · cancel anytime
          </p>
          <h1 className="mt-7 text-5xl font-black leading-[1.02] tracking-[-0.03em] sm:text-6xl lg:text-[4.25rem]">
            Pay $5 for
            <br />
            the $50 thing.
          </h1>
          <p className="mx-auto mt-6 max-w-xl text-lg text-slate-600 sm:text-xl lg:mx-0">
            Retailers hide their deepest clearance. DealHawk finds it: live prices, real stock, aisle-level
            directions at stores near you.
          </p>
          <div className="mt-8 flex flex-col items-center justify-center gap-3 sm:flex-row lg:justify-start">
            <Link
              to="/get-started"
              className="inline-flex h-14 w-full items-center justify-center rounded-2xl bg-[#7C3AED] px-8 text-lg font-extrabold text-white sm:w-auto"
            >
              Get started
            </Link>
            <Link
              to="/deals"
              className="inline-flex h-14 w-full items-center justify-center rounded-2xl border border-slate-200 px-8 text-lg font-extrabold sm:w-auto"
            >
              See free online deals
            </Link>
          </div>
          <div className="mt-9">
            <Stats />
          </div>
        </div>
        <div className="relative mx-auto w-full max-w-[280px] sm:max-w-[320px] lg:ml-auto">
          <div className="rounded-[2.75rem] border border-slate-200 bg-white p-3 shadow-[0_30px_90px_-25px_rgba(124,58,237,0.45)]">
            <video
              autoPlay
              muted
              loop
              playsInline
              preload="metadata"
              poster="/hero-app-demo-poster.jpg"
              className="h-auto w-full rounded-[2.25rem] border border-slate-100"
              aria-label="DealHawk app demo"
            >
              <source src="/hero-app-demo.webm" type="video/webm" />
              <source src="/hero-app-demo.mp4" type="video/mp4" />
            </video>
          </div>
        </div>
      </div>
      <Retailers />
    </section>
  );
}

const HEROES: Record<OptionId, () => ReactElement> = {
  feed: FeedHero,
  hunter: HunterHero,
  local: LocalHero,
  haul: HaulHero,
  current: CurrentHero,
};

export default function LandingOptionsPage() {
  useEnforceLightMode();
  const [params, setParams] = useSearchParams();
  const requested = params.get("v");
  const selected: OptionId = isOption(requested) ? requested : "feed";
  const Hero = HEROES[selected];
  const dark = selected === "hunter";

  usePageBackground(dark ? DARK : "#FFFFFF", dark ? DARK : "#FFFFFF");

  return (
    <div className={`min-h-screen ${DISPLAY} ${dark ? "bg-[#201B33] text-white" : "bg-white text-[#1B1526]"}`}>
      <div className="sticky top-0 z-50 border-b border-white/10 bg-[#1B1526] text-white">
        <div className="mx-auto flex max-w-6xl flex-wrap items-center gap-2 px-4 py-3 sm:px-6">
          <p className="mr-2 text-[11px] font-bold uppercase tracking-[0.18em] text-white/45">
            Preview
          </p>
          {OPTIONS.map((option) => (
            <button
              key={option.id}
              type="button"
              onClick={() => setParams({ v: option.id })}
              className={`rounded-full px-3 py-1.5 text-sm font-bold ${
                selected === option.id ? "bg-white text-[#1B1526]" : "text-white/70 hover:text-white"
              }`}
            >
              {option.label}
            </button>
          ))}
        </div>
      </div>
      <Hero />
    </div>
  );
}
